import { useEffect } from 'react';

import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import type { PinRefusal } from '@/lib/enrolmentSession';
import { useEnrolmentSession } from '@/lib/enrolmentSession';

import { ENROLMENT_COPY, RECORDED_COPY, RULED_COPY } from '../copy';
import { EnrolmentHeading } from './EnrolmentHeading';
import { EnrolmentShell } from './EnrolmentShell';
import { Keypad } from './Keypad';
import { PinDots } from './PinDots';

/**
 * The PIN pad — frames *01 · Enter PIN*, *02 · Entering*, *03 · Error (2 left)* and *04 · Error (1 left)*.
 *
 * **They are one screen in four states, which is how the file draws them**: the same heading block, the same
 * six slots, the same keypad and the same `No sign-in` note, with an inline `Callout` appearing between the
 * heading and the slots when the server has refused. `02` differs from `01` only by two filled dots.
 *
 * ⛔ **There is no Submit button** [AC-2] and no venue picker, site list or confirmation step anywhere in
 * this flow [AC-7b; decision 0148] — the frame that once drew one is retired and deleted from the file.
 */
export function PinScreen() {
  const digits = useEnrolmentSession((state) => state.digits);
  const refusal = useEnrolmentSession((state) => state.refusal);
  const issueToken = useEnrolmentSession((state) => state.issueToken);
  const pressDigit = useEnrolmentSession((state) => state.pressDigit);
  const pressBackspace = useEnrolmentSession((state) => state.pressBackspace);
  /*
   * ⭐ **The boot's no-answer state is this same pad** [03-us-kad-6i AC-10; gap G2], and the only thing that
   * distinguishes it is a way back. ⛔ **It renders on nothing else** — not on a refused PIN, not on a
   * first load, and not on any frame the design draws.
   */
  const probeUnanswered = useEnrolmentSession((state) => state.probeUnanswered);
  const recheckEnrolment = useEnrolmentSession((state) => state.recheckEnrolment);

  /*
   * ⭐ **The layer-1 token is fetched on load, before any digit** [AC-1b; KAD-6b AC-1; decision 0203].
   *
   * The three-strike device lock is keyed to it, so without this call the countdown on frames 03 and 04
   * counts against nothing and the lock has no subject — the security control absent while every screen
   * still looks right. The app never reads the token: it is an `HttpOnly` cookie, and this call exists only
   * to make the browser hold one.
   */
  useEffect(() => {
    void issueToken('mount');
  }, [issueToken]);

  /*
   * ⭐ **Typed digits count as well as taps** [AC-3; decision 0223]. The ruled fleet is Chromebooks and
   * Surfaces, which have keyboards — and the same Chromebook in tablet mode has none, which is why the
   * keypad stays. On `window` rather than on an input: the pad has no text field to focus, and after a tap
   * the focus sits on a keypad button, where a `keydown` bound to the field would never be heard.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      /*
       * ⛔ **A resting finger is not six digits.** The OS repeats a held key ~30 times a second, and the
       * sixth repeat auto-submits [AC-2] — so a Chromebook with something leaning on the keyboard enters
       * `111111`, spends a strike nobody typed, and two more like it reach a lock only an admin clears
       * [0052; 0203]. ⛔ **And a modified key is a shortcut, not an entry**: Ctrl+1 switches a tab.
       */
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === 'Backspace') {
        // Or the browser treats it as *go back*, which on a kiosk is a screen nobody asked for.
        event.preventDefault();
        pressBackspace();
        return;
      }
      pressDigit(event.key);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [pressDigit, pressBackspace]);

  return (
    <EnrolmentShell>
      <div className="flex w-full flex-col items-center gap-6 pb-8 pt-2">
        <EnrolmentHeading
          body={ENROLMENT_COPY.body}
          eyebrow={ENROLMENT_COPY.eyebrow}
          heading={ENROLMENT_COPY.heading}
        />

        {refusal !== undefined && (
          /*
            `role="alert"`: the slots clear and the message appears with no navigation event to carry it, so
            without a live region a screen-reader user is told nothing at all about a failed attempt [AC-10].
          */
          <div className="w-full" role="alert">
            <Callout tone="warning">{messageFor(refusal)}</Callout>
          </div>
        )}

        <PinDots entered={digits.length} />
        <Keypad onBackspace={pressBackspace} onDigit={pressDigit} />

        {probeUnanswered && digits.length === 0 && (
          /*
            ⛔ **UNDRAWN, and it is a criterion rather than an invention** [AC-10]: *"exactly ONE bounded,
            user-visible recovery … an explicit Try again affordance that re-issues exactly one probe"*.
            ⚠️ **The `online` / `visibilitychange` half of that criterion cannot reach the outage the
            recovery exists for** — a server-side blip leaves the device's link up, so neither event ever
            fires and a branch of tablets sits on an unexplained pad until somebody types a PIN at each one.

            Assembled from the design system's own primitive at its own size; ⛔ no new visual language, and
            ⛔ no sentence beside it, because what an unreadable boot SAYS is gap **G2** and is the
            Operator's. ⛔ **It is not on any drawn frame**: `probeUnanswered` is false on every seeded
            state, so the fidelity gate measures the pad the file draws.

            ⛔ **And it leaves the moment a digit is typed.** It is the keypad's next sibling, so a mis-tap
            under the bottom row would otherwise throw away a half-entered PIN with no confirmation — the
            press clears the state on its way back to the spinner. It returns as soon as a backspace empties
            the field again.
          */
          <Button onClick={() => void recheckEnrolment('pressed')} variant="secondary">
            {RECORDED_COPY.bootRetry}
          </Button>
        )}

        <p className="w-full text-center text-xs leading-5 text-[var(--muted-foreground)]">
          {ENROLMENT_COPY.noSignIn}
        </p>
      </div>
    </EnrolmentShell>
  );
}

/**
 * The server's last answer, as the screen says it — one inline error, in one place, for every refusal.
 *
 * ⭐ **Two of these are drawn copy and four are the Operator's ruling of 2026-09-16** [0261, 0262, 0263].
 * The drawn pair is chosen by the count the SERVER reported, never by anything this device counted
 * [AC-4; § *The invariant*]; the rest say as little as the state allows, because *nothing disclosed* is the
 * principle the ruling states. ⛔ None of them renders *Device locked* and none renders a countdown [AC-6].
 *
 * ⭐ **Both 429s return the SAME string**, so which control refused cannot be read off the screen — see
 * `RULED_COPY.tooManyAttempts`.
 *
 * ⚠️ **`attemptsLeft` outside 2 and 1 says *Incorrect PIN*.** The api sends `null` when nothing counted the
 * attempt — no layer-1 token, or layer 1's store failed open — and `0` only on the lock, which is a 403 and
 * a different screen. The PIN really was wrong, and the two drawn strings both embed a count this screen
 * would have had to invent, so it falls to the ruled string that says the true part and no more.
 */
function messageFor(refusal: PinRefusal): string {
  if (refusal.kind === 'throttled' || refusal.kind === 'rate-limited') {
    return RULED_COPY.tooManyAttempts;
  }
  if (refusal.kind === 'unreadable') return RULED_COPY.havingIssues;
  if (refusal.kind === 'credential-refused') return RULED_COPY.credentialRefused;

  if (refusal.attemptsLeft === 2) return ENROLMENT_COPY.twoAttemptsLeft;
  if (refusal.attemptsLeft === 1) return ENROLMENT_COPY.oneAttemptLeft;
  return RULED_COPY.credentialRefused;
}
